<template>
  <section :class="['value-calculator', brand]" :style="cssProps">
    <AppContainer class="value-calculator__crops fujimori" size="xl" spacing="lg">
      <AppHeading element="h3" size="xs" spacing="md" class="select-crop-heading">
        Select Your Crop
      </AppHeading>
      <CropList v-model="selectedCropValue" :crops="cropNames" />
    </AppContainer>

    <AppContainer size="xl" spacing="lg">
      <AppFlexRow>
        <AppFlexCol class="value-calculator__flex-col">
          <div class="value-calculator__roi">
            <CircularProgress :value="chartValuePlaceholder">
              <img v-if="this.brand === 'microessentials'" src="//images.ctfassets.net/3jf1t80tlft4/2sdm4Hj3jXfBdUrJERJcQG/bb3142cfaf3e790d42b8ad15e377df57/MicroEssentials.png" class="microessential-image" />
              <img v-else src="//images.ctfassets.net/3jf1t80tlft4/4D2oGEZbRMT4T6xHM2mjHo/b84bfac9f6e0248517e8d2d4e71f7d0a/AspireLogoBlack.svg" class="aspire-image" />
              <h3 class="value-calculator__increase-subheading growler-heading">{{ roiPlaceholder }}</h3>
              <h2 :class="['value-calculator__increase-message', messageElement, 'microColor', this.brand === 'microessentials']" class="growler-text">
                  {{ increaseMessage }}
              </h2>
            </CircularProgress>
          </div>

          <AppFlexRow>
            <AppFlexCol class="value-calculator__add-col value-calculator__add-col--bordered additional-col">
              <AppHeading element="h5" size="xs" spacing="sm" class="additional-text">
                Additional revenue
              </AppHeading>
              <AppHeading :class="['microColor', this.brand === 'microessentials']" element="h4" size="sm" class="additional-placeholder">
                <TextContent :content="additionalRevenuePlaceholder" />
              </AppHeading>
            </AppFlexCol>
            <AppFlexCol class="value-calculator__add-col value-calculator__add-col--bordered additional-col">
              <AppHeading element="h5" size="xs" spacing="sm" class="additional-text">
                Additional investment
              </AppHeading>
              <AppHeading :class="['microColor', this.brand === 'microessentials']" element="h4" size="sm" class="additional-placeholder">
                <TextContent :content="additionalInvestmentPlaceholder" />
              </AppHeading>
            </AppFlexCol>
          </AppFlexRow>
          <div :class="['value-calculator__button-container', buttonHide, buttonRemove]">
            <div class="value-calculator__dynamic-holder">
              <AppButton icon="ArrowRight" :class="['button--border', 'value-calculator__adjust-btn', btnOneHide, btnOneRemove]" disabled>
                Adjust Inputs to Calculate
              </AppButton>
              <AppButton icon="ArrowRight" :class="['button--border', 'value-calculator__click-btn', 'calculate-button', btnTwoHide, btnTwoRemove]" @click="calculateRoi">
                {{ buttonText }}
              </AppButton>
            </div>
          </div>
          <AppParagraph class="value-calculator__disclaimer disclaimer" size="xs" theme="gray">
            <TextContent :content="selectedFertilizer.disclaimer" />
          </AppParagraph>
        </AppFlexCol>

        <AppFlexCol class="value-calculator__flex-col">
          <div class="value-calculator__input value-calculator__input--price">
            <AppHeading element="h3" size="sm" spacing="md" class="growler-heading">
              Acres in production?
            </AppHeading>

            <AppHeading element="h4" size="md">
              <TextContent :content="selectedAcreage" class="growler-text"/>
              <small>
                <TextContent :content="acreageUnit" class="growler-unit"/>
              </small>
            </AppHeading>

            <div class="value-calculator__slider">
              <VueSlider
                v-model="selectedAcreage"
                :dotSize="24"
                :interval="1"
                :height="2"
                :min="100"
                :max="5000"
                :silent="true"
                tooltip="none"
              />
            </div>

            <AppParagraph size="sm" class="helper-text">
              Adjust acreage to match your operation.
            </AppParagraph>
          </div>

          <div class="value-calculator__input value-calculator__input--price">
            <AppHeading element="h3" size="sm" spacing="md" class="growler-heading">
              How much can you expect to get for your crop?
            </AppHeading>

            <AppHeading element="h4" size="md">
              $<TextContent :content="displayPrice" class="growler-text"/>
              <small> / <TextContent :content="selectedCrop.priceUnit" class="growler-unit"/> </small>
            </AppHeading>

            <div class="value-calculator__slider">
              <VueSlider
                v-model="selectedPrice"
                :dotSize="24"
                :interval="selectedCrop.priceInterval"
                :height="2"
                :min="selectedCrop.minimumPrice"
                :max="selectedCrop.maximumPrice"
                :silent="true"
                tooltip="none"
              />
            </div>

            <AppParagraph size="sm" class="helper-text">
              Default crop prices are an estimate. Local prices may vary.
            </AppParagraph>
          </div>

          <div class="value-calculator__input value-calculator__input--formula">
            <AppHeading element="h3" size="sm" spacing="md" class="growler-heading">
              Formula Comparison
            </AppHeading>

            <template v-if="formulas.length > 0">
              <AppParagraph spacing="md" class="helper-text">
                We have the following formulas for 
                <TextContent :content="selectedCrop.crop" />:
              </AppParagraph>
              <div class="value-calculator__bubble_button_wrapper">
                <label
                  v-for="formula in formulas"
                  :key="formula.id"
                  class="value-calculator__bubble_button"
                  :class="{
                    'value-calculator__bubble_button--selected': isFormulaSelected(formula.id)
                  }"
                >
                  <AssetEmbed :asset="formula.icon || formula.logo" class="value-calculator__bubble_image" />
                  <input v-model="selectedFormulaId" :value="formula.id" type="radio" />
                </label>
              </div>
            </template>

            <AppParagraph spacing="md" class="helper-text">
              We have trial results comparing
              <TextContent :content="selectedFormula.name" /> to:
            </AppParagraph>
            <div class="value-calculator__bubble_button_wrapper">
              <label
                v-for="fertilizer in fertilizers"
                :key="fertilizer.id"
                class="value-calculator__bubble_button"
                :class="{
                  'value-calculator__bubble_button--selected': isFertilizerSelected(fertilizer.id)
                }"
              >
                <TextContent :content="fertilizer.name" />
                <input v-model="selectedFertilizerId" :value="fertilizer.id" type="radio" />
              </label>
            </div>
          </div>

          <div class="value-calculator__input value-calculator__input--yield">
            <AppHeading element="h3" size="sm" spacing="md" class="growler-heading">
              Expected yield advantage
            </AppHeading>
            <AppHeading element="h4" size="md">
              <TextContent :content="selectedYield" class="growler-text" />
              <small>
                <TextContent :content="selectedCrop.priceUnit" class="growler-unit" /> /
                <TextContent :content="selectedFertilizer.yieldUnit" class="growler-unit" />
              </small>
            </AppHeading>
            <div class="value-calculator__slider">
              <VueSlider
                v-model="selectedYield"
                :dotSize="24"
                :interval="selectedFertilizer.yieldInterval"
                :height="2"
                :min="selectedFertilizer.minimumYield"
                :max="selectedFertilizer.maximumYield"
                :silent="true"
                tooltip="none"
              />
            </div>
          </div>
        </AppFlexCol>
      </AppFlexRow>
    </AppContainer>
  </section>
</template>

<script>
import _ from 'lodash';

import AppButton from '@/components/shared/AppButton';
import AppContainer from '@/components/shared/AppContainer';
import AppHeading from '@/components/shared/AppHeading';
import AppSubheading from '@/components/shared/AppSubheading';
import AppParagraph from '@/components/shared/AppParagraph';
import AppFlexRow from '@/components/shared/AppFlexRow';
import AppFlexCol from '@/components/shared/AppFlexCol';
import AssetEmbed from '@/components/shared/AssetEmbed';
import CropList from '@/components/shared/CropList';
import CircularProgress from '@/components/shared/CircularProgress';
import TextContent from '@/components/shared/TextContent';
import VueSlider from 'vue-3-slider-component';
import { populateSubscriberKey, dispatchCalculateEvent } from '@/sfmc/campaign-utilities';

export default {
  components: {
    AppButton,
    AppContainer,
    AppHeading,
    AppSubheading,
    AppParagraph,
    AppFlexRow,
    AppFlexCol,
    AssetEmbed,
    CropList,
    CircularProgress,
    TextContent,
    VueSlider
  },
  props: {
    backgroundTheme: {
      type: String,
      required: true,
      validator: value => ['white', 'light', 'black'].includes(value)
    },
    valueCalculator: {
      type: Object,
      required: true
    },
    brand: {
      type: String,
      required: true
    }
  },
  data() {
    const fertilizer = this.valueCalculator.fertilizers.filter(
      f => f !== undefined && f.crop !== undefined && f.formula !== undefined
    )[0];

    return {
      isIntialized: false,
      selectedCropId: fertilizer.crop.id,
      selectedFormulaId: fertilizer.formula.id,
      selectedFertilizerId: fertilizer.id,
      selectedPrice: fertilizer.crop.defaultPrice,
      selectedYield: fertilizer.defaultYield,
      selectedAcreage: 0,
      buttonText: 'Calculate Your Results',
      disclaimer: '*Results based on 70 lbs of P₂O₅ applied and a premium of $80/ton over DAP and balanced for total N. This example reflects typical use rates. Actual yield results and local pricing may vary. Expected yield advantage based on multi-year research conducted by The Mosaic Company.',
      increaseMessage: '-',
      acreageUnit: 'AC',
      chartValuePlaceholder: 0,
      roiPlaceholder: 'ROI Increase',
      additionalRevenuePlaceholder: '-',
      additionalInvestmentPlaceholder: '-',
      buttonHide: '',
      buttonRemove: '',
      btnOneHide: '',
      btnOneRemove: '',
      btnTwoHide: 'hide',
      btnTwoRemove: 'remove',
      subscriberkey: '',
      emailAddress: '',
      firstName: '',
      messageElement: [],
      additionalElements: '',
      wasClicked: false,
      micro: '#F5A92B',
      aspire: '#84B854',
      pfTrack: 'organic'
    };
  },
  computed: {
    chartValue() {
      return (this.revenue / (this.revenue + this.selectedFertilizer.cost)) * 100;
    },
    crops() {
      const crops = this.valueCalculator.fertilizers
        .filter(f => f !== undefined && f.crop !== undefined && f.formula !== undefined)
        .map(f => f.crop);

      return _.uniqBy(crops, 'id');
    },
    cropNames() {
      return this.crops.map(c => c.crop);
    },
    formulas() {
      const formulas = this.valueCalculator.fertilizers
        .filter(f => f !== undefined && f.crop !== undefined && f.formula !== undefined)
        .filter(f => f.crop.id === this.selectedCropId)
        .map(c => c.formula);

      return _.uniqBy(formulas, 'id');
    },
    fertilizers() {
      var fert = this.valueCalculator.fertilizers.filter(
        f => f !== undefined && f.crop !== undefined && f.formula !== undefined
      );
      return _.uniqBy(
        fert.filter(f => f.crop.id === this.selectedCropId).filter(f => f.formula.id === this.selectedFormulaId),
        'id'
      );
    },
    returnOnInvestment() {
      return (this.revenue - this.selectedFertilizer.cost) * this.selectedAcreage;
    },
    revenue() {
      return this.selectedPrice * this.selectedYield;
    },
    additionalRevenue() {
      return this.revenue * this.selectedAcreage;
    },
    additionalInvestment() {
      return this.selectedFertilizer.cost * this.selectedAcreage;
    },
    selectedCrop() {
      return this.crops.find(c => c.id === this.selectedCropId);
    },
    selectedCropValue: {
      get() {
        return this.selectedCrop.crop;
      },
      set(crop) {
        this.selectedCropId = this.crops.find(c => c.crop === crop).id;
      }
    },
    selectedFormula() {
      return this.formulas.find(f => f.id === this.selectedFormulaId);
    },
    selectedFormulaName() {
      return this.formulas.find(f => f.id === this.selectedFormulaId).name;
    },
    selectedFertilizer() {
      return this.valueCalculator.fertilizers.filter(f => f !== undefined && f.crop !== undefined && f.formula !== undefined)
      .find(f => f.id === this.selectedFertilizerId);
    },
    cssProps() {
      return {
        '--brand-color': this.brand === 'microessentials' ? this.micro : this.aspire
      };
    },
    displayPrice() {
      return parseFloat(this.selectedPrice).toFixed(2);
    }
  },
  watch: {
    fertilizers(fertilizers) {
      if(typeof fertilizers !== 'undefined' && fertilizers.length > 0) {
        this.selectedFertilizerId = fertilizers[0].id;
      }
    },
    formulas(formulas) {
      this.selectedFormulaId = _.uniqBy(formulas, 'id')[0].id;
    },
    selectedCrop(selectedCrop) {
      this.selectedPrice = selectedCrop.defaultPrice;
      this.resetValues();
    },
    selectedFertilizer(selectedFertilizer) {
      this.selectedYield = selectedFertilizer.defaultYield;
    },
    selectedAcreage(selectedAcreage) {
      if (selectedAcreage === 0) {
        this.btnTwoHide = 'hide';
        setTimeout(() => {
          this.btnTwoRemove = 'remove';
          this.btnOneRemove = '';
          this.btnOneHide = '';
        }, 250);
      } else {
        this.btnOneHide = 'hide';
        setTimeout(() => {
          this.btnOneRemove = 'remove';
          this.btnTwoRemove = '';
          this.btnTwoHide = '';
        }, 250);
      }
    }
  },
  beforeMount() {
    this.chartValuePlaceholder = 0;
  },
  mounted() {
    window.addEventListener('message', this.getPostMessage);
  },
  methods: {
    resetValues() {
      this.selectedAcreage = 0;
      this.roiPlaceholder = 'ROI Increase',
      this.additionalRevenuePlaceholder = '-',
      this.additionalInvestmentPlaceholder = '-',
      this.increaseMessage = '-',
      this.chartValuePlaceholder = 0    
    },
    getPostMessage(event) {
      if (event.origin === 'https://explore.cropnutrition.com') {
        console.log('received message from: ' + event.origin);
        console.log(JSON.stringify(event.data, null, 4));
        if (event.data['event_name'] === 'pass_form_data') {
          this.subscriberKey = event.data['SubscriberKey'];
          this.emailAddress = event.data['EmailAddress'];
          this.firstName = event.data['FirstName'];
          this.pfTrack = event.data['pf_track'];

          this.setAcres(event.data['Acres']);
          this.setCrops(event.data['Crops']);
        }
      }
    },
    setAcres(acres) {
      if (acres === '2500+') {
        this.selectedAcreage = 5000;
      } else {
        const arr = acres.split('-');
        this.selectedAcreage = arr[1] * 1;
      }
    },
    setCrops(crops) {
      const options1 = ['Corn', 'Soybean', 'Canola', 'Wheat', 'Cotton', 'Rice'];
      const options2 = ['Cotton', 'Corn', 'Soybean', 'Alfalfa', 'Potato'];
      const opts = this.brand === 'microessentials' ? options1 : options2;
      const arr = crops.split(';');
      const cropList = [];

      arr.forEach(el => {
        if (el.indexOf(' ') >= 0) {
          const temp = el.split(' ');
          if (opts.includes(temp[1])) {
            cropList.push(temp[1]);
          }
        } else {
          if (opts.includes(el)) {
            cropList.push(el);
          }
        }
      });
      if (cropList.length > 0) {
        this.selectedCropValue = cropList[0];
      }
    },
    setFertilizerBrand() {
      let fBrand = '';
      if (this.pfTrack === 'organic') {
        fBrand = this.brand === 'microessentials' ? 'micro-run-my-numbers' : 'aspire-run-my-numbers';
      } else {
        fBrand = this.brand === 'microessentials' ? 'micro-run-my-numbers-dtn' : 'aspire-run-my-numbers-dtn';
      }
      return fBrand;
    },
    isCropSelected(id) {
      return id === this.selectedCropId;
    },
    isFertilizerSelected(id) {
      return id === this.selectedFertilizerId;
    },
    isFormulaSelected(id) {
      return id === this.selectedFormulaId;
    },
    calculateRoi() {
      dispatchCalculateEvent();
      if (!this.wasClicked) {
        // keeping this commented for now
        // this.sendMessage();

        this.buttonHide = 'hide';
        this.additionalElements = 'hide';
        this.wasClicked = true;
        setTimeout(() => {
          this.buttonRemove = 'remove';
          this.increaseMessage = '$' + Math.round(this.returnOnInvestment).toLocaleString();
          this.messageElement.push('transforward');
          this.additionalRevenuePlaceholder = '$' + Math.round(this.additionalRevenue).toLocaleString();
          this.additionalInvestmentPlaceholder = '$' + Math.round(this.additionalInvestment).toLocaleString();
          this.chartValuePlaceholder = this.chartValue;
          this.additionalElements = '';
          this.selectedAcreage = 0;
        }, 250);
        setTimeout(() => {
          this.buttonRemove = '';
          setTimeout(() => {
            this.buttonHide = '';
          }, 25);
        }, 1500);
      } else {
        // keeping this commented for now
        // this.sendMessage();
        
        this.buttonHide = 'hide';
        this.additionalElements = 'hide';
        this.messageElement.push('hide');
        setTimeout(() => {
          this.buttonRemove = 'remove';
          this.increaseMessage = '$' + Math.round(this.returnOnInvestment).toLocaleString();
          this.messageElement.pop();
          this.additionalRevenuePlaceholder = '$' + Math.round(this.additionalRevenue).toLocaleString();
          this.additionalInvestmentPlaceholder = '$' + Math.round(this.additionalInvestment).toLocaleString();
          this.chartValuePlaceholder = this.chartValue;
          this.additionalElements = '';
          this.selectedAcreage = 0;
        }, 250);
        setTimeout(() => {
          this.buttonRemove = '';
          setTimeout(() => {
            this.buttonHide = '';
          }, 25);
        }, 1500);
      }
    },
    sendMessage() {
      const protocol  = location.protocol;
      const hostname  = location.hostname;
      const restBase  = protocol + '//' + hostname + '/wp-json/vue_module/v1';

      const data = {
        From: {
          Address: 'mosaic.marketing@cropnutrition.mosaicco.com',
          Name: 'The Mosaic Company'
        },
        To: {
          Address: this.emailAddress,
          SubscriberKey: populateSubscriberKey(this.subscriberKey, this.emailAddress),
          ContactAttributes: {
            SubscriberAttributes: {
              SubscriberKey: populateSubscriberKey(this.subscriberKey, this.emailAddress),
              EmailAddress: this.emailAddress,
              FirstName: this.firstName,
              Campaign: this.brand === 'microessentials' ? 'me22_23' : 'asp22_23',
              Source: 'mos',
              Content: 'testcontent',
              Medium: 'email',
              SelectedCrop: this.selectedCropValue,
              SelectedAcreage: this.selectedAcreage,
              ExpectedPrice: this.selectedPrice.toFixed(2) * 1,
              SelectedFormula: this.selectedFormulaName,
              ComparedToFormula: this.selectedFertilizer.name,
              ExpectedYield: this.selectedYield,
              ReturnOnInvestment: Math.round(this.returnOnInvestment),
              AdditionalRevenue: Math.round(this.additionalRevenue),
              AdditionalInvestment: Math.round(this.additionalInvestment)
            }
          },
          Options: {
            RequestType: 'SYNC'
          }
        }
      };

      const request_data = {
          'action': 'run-my-numbers',
          'key': this.setFertilizerBrand(),
          'send_body': data
        };

        const options = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': window.vue_module_data.apiNonce
          },
          credentials: 'same-origin',
          body: JSON.stringify(request_data)
        };

        fetch(restBase + '/send', options)
            .then(response => response.json())
            .then(data => {
                console.log(data);
            })
            .catch(error => {
                console.error('Error:', error);
            });
    }
  }
};
</script>

<style lang="scss" scoped>
@keyframes transforward {
  from {
    font-size: 1.125rem;
  }

  to {
    font-size: 3.5rem;
  }
}

@keyframes transout {
  from {
    font-size: 2.125rem;
  }

  to {
    font-size: 0;
  }
}
.value-calculator {
  &__heading-container,
  &__crops {
    text-align: center;
  }

  &__heading-container {
    @include font('open-sans', 'normal');
  }

  &__crops {
    border-bottom: solid 1px color('tertiary');
    border-top: solid 1px color('tertiary');
    margin: spacing('xl') 0;
    padding: spacing('xl') 0;
  }

  &__flex-col {
    padding: 0;

    @include breakpoint('md') {
      padding: 0 spacing('xl');
    }

    &:first-child {
      order: 2;
      padding-left: 0;

      @include breakpoint('md') {
        order: 1;
      }
    }

    &:last-child {
      border-right: 0;
      order: 1;
      padding-right: 0;

      @include breakpoint('md') {
        order: 2;
      }
    }
  }

  &__input {
    margin: spacing('xl') 0;

    &:first-child {
      margin-top: 0;
    }
  }

  &__calculate-email-note {
    padding-bottom: 0.875rem;
  }

  small {
    font-family: $articulat-cf;
    font-size: 22px;
    margin-left: .5rem;
  }

  .value-calculator__increase-subheading {
    color: #000;
    font-size: 1.125rem;
    margin: 24px 0 0;
    opacity: 1;
    text-transform: uppercase;
    transition: opacity 0.3s ease-out;

    @media (max-width: 320px) {
      margin: 0;      
    }

    &.transforward,
    &.transback {
      animation-direction: normal;
      animation-duration: 0.3s;
      animation-fill-mode: forwards;
    }
    &.transforward {
      animation-name: transforward;
    }
    &.transback {
      animation-duration: 0.3s;
      animation-name: transback;
    }
    &.hide {
      opacity: 0;
      transition: opacity 0.3s ease-in;
    }
  }

  .value-calculator__increase-message {
    @include font('trade-gothic', 'bold');
    color: #000;
    font-size: 2.125rem;
    line-height: 1.1;
    max-width: 290px;
    padding-left: 0.75rem;
    text-indent: -1.5rem;
    text-transform: uppercase;

    @media (min-width: 420px) {
      color: var(--brand-color);
    }

    &.transout {
      animation-direction: normal;
      animation-duration: 0.3s;
      animation-fill-mode: forwards;
      animation-name: transout;
      opacity: 0;
      transition: opacity 0.15s ease-out;
    }
    &.remove {
      display: none;
    }
  }
  .progress-circle__svg {
    opacity: 0.6;

    @media (min-width: 420px) {
      opacity: 1;
    }
  }

  &__bubble_button_wrapper {
    display: flex;
    margin-bottom: spacing('md');
  }

  &__bubble_button {
    font-family: $inter;
    align-items: center;
    border: solid 1.5px shade('white', 10%);
    display: flex;
    font-size: 16px;
    font-weight: 700;
    height: 4rem;
    justify-content: center;
    line-height: 1;
    margin: 0 spacing('xs');
    text-align: center;
    width: 4rem;

    &--selected {
      border-color: var(--brand-color);
    }

    input {
      display: none;
    }
  }

  &__bubble_image {
    width: 2.375rem;
  }

  &__roi {
    @include breakpoint('sm') {
      padding: 0 spacing('xl', 4);
    }

    @include breakpoint('md') {
      padding: 0;
    }
  }

  &__add-col {
    padding: spacing('lg') 0;
    text-align: center;

    h4 {
      transition: opacity 0.3s ease-out;
      &.hide {
        opacity: 0;
        transition: opacity 0.3s ease-in;
      }
    }
  }

  &__dynamic-holder {
    display: flex;
    justify-content: center;
    position: absolute;
    width: 100%;
  }

  &__button-container {
    align-items: center;
    display: flex;
    flex-direction: column;
    margin-bottom: 2.5rem;
    margin-top: 1.5rem;
    min-height: 53px;
    position: relative;
    transition: background-color 0.25s ease-out;

    @include breakpoint('md') {
      margin-bottom: 5rem;
      margin-top: 3.5rem;
    }

    .button--border.button--primary {
      transition: opacity 0.25s ease-out;
      &:disabled,[disabled] {
        background-color: black;
        color: white;
      }
      &:hover {
        background-color: black;
        color: white;
        transition: background-color 0.25s ease-in;
      }
    }
    h3 {
      display: none;
      opacity: 0;
      transition: opacity 0.25s ease-out;
    }

    &.hide {
      .button--border.button--primary {
        opacity: 0;
        transition: opacity 0.25s ease-in;
      }
    }

    &.remove {
      .button--border.button--primary {
        display: none;
      }
    }
    &.unhide {
      h3 {
        display: block;
      }
    }
    &.show {
      h3 {
        opacity: 1;
        transition: opacity 0.25s ease-in;
      }
    }
  }

  &__adjust-btn,
  &__click-btn {
    transition: opacity .25s ease-in-out;
    &.hide {
      opacity: 0;
    }

    &.remove {
      display: none;
    }
  }

  &__disclaimer {
    margin-bottom: 2rem;

    @include breakpoint('md') {
      margin-bottom: 0;
    }
  }

  &__slider {
    margin: spacing('md') 0;

    
    :deep(.vue-slider) {
      z-index: 0;
    }

    :deep(.vue-slider-rail) {
      background-color: shade('white', 10%);
    }

    :deep(.vue-slider-process) {
      background-color: var(--brand-color);
      border-radius: 0;
    }

    :deep(.vue-slider-dot-handle) {
      background-color: var(--brand-color);
      border: solid 2px var(--brand-color);
      box-shadow: none;
    }
    
  }
  .select-crop-heading {
    font-family: $inter;
    font-size: 24px;
    font-weight: 800;
    line-height: 28px;
    text-align: left;
    text-transform: none;

    @include breakpoint('md') {
      font-size: 28px;
      font-weight: 700;
      line-height: 30px;
    }
  }
  .growler-heading {
    font-family: $articulat-cf;
    font-size: 20px;
    font-weight: 700;
    line-height: 24px;
    text-transform: none;

    @include breakpoint('md') {
      font-size: 28px;
      font-weight: 700;
      line-height: 30px;
    }
  }
  .growler-text {
    font-family: $articulat-cf;
    font-size: 48px;
    font-weight: 500;
    line-height: 50px;

    @include breakpoint('md') {
      font-size: 64px;
      font-weight: 400;
      line-height: 95px;
    }

    @media (max-width: 320px) {
      color: var(--brand-color);
      font-size: 30px;
      font-weight: 900;
      line-height: 40px;
      padding-left: 0;
      text-indent: 0;
    }
  }
  .growler-unit {
    font-family: $articulat-cf;
    font-size: 20px;
    font-weight: 700;
    line-height: 30px;

    @include breakpoint('md') {
      font-size: 28px;
      font-weight: 700;
      line-height: 30px;
    }
  }
  .helper-text {
    font-family: $inter;
    font-size: 11px;
    font-weight: 400;
    line-height: 17px;

    @include breakpoint('md') {
      font-size: 13px;
      font-weight: 400;
      line-height: 23px;
    }
  }
  .additional-text {
    font-family: $articulat-cf;
    font-size: 16px;
    font-weight: 700;
    line-height: 16px;
    text-transform: none;

    @include breakpoint('md') {
      font-size: 20px;
      font-weight: 700;
      line-height: 16px;
    }
  }
  .additional-placeholder {
    color: var(--brand-color);
    font-family: $articulat-cf;
    font-size: 24px;
    font-weight: 900;
    line-height: 27px;
    text-transform: none;

    @include breakpoint('md') {
      font-size: 40px;
      font-weight: 700;
      line-height: 44px;
    }
  }
  .additional-col {
      width: 50%;

    @include breakpoint('md') {
      width: 100%;
    }
  }
  .disclaimer {
    color: color('inactive-grey');
    font-family: $inter;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    text-align: center;
  }
  .aspire-image {
    height: 40px;
    margin-top: 0.5rem;
    width: 60px;
    
    @include breakpoint('md') {
      height: 62px;
      margin-top: 0;
      width: 128px;
    }
  }
  .microessential-image {
    height: auto;
    margin-top: 0.5rem;
    width: 100px;
    
    @include breakpoint('md') {
      height: auto;
      margin-top: 0;
      width: 128px;
    }
  }
}
</style>
